import { useMemo, useRef } from 'react';
import {
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
} from '@mui/material';
import { useDateSort } from '../../../hooks/useDateSort';
import TableActionsMenu from '../../common/TableActionsMenu';
import { NaverDirectDetailStats, NaverProviderCampaign, getNaverCampaignLabel } from '../../../types/naver.types';
import type { Dayjs } from 'dayjs';

type Props = {
  data: NaverDirectDetailStats[];
  providerCampaigns: NaverProviderCampaign[];
  startDate: Dayjs;
  endDate: Dayjs;
};

type CampaignMetrics = {
  click: number;
  postbackSuccess: number;
  conversionRate: number;
  appliedUnitPrice: number | null;
  revenue: number;
};

type PivotRow = {
  date: string;
  [key: string]: CampaignMetrics | string;
};

const TOTAL_PROVIDER = '전체';

const formatCurrency = (value: number) =>
  new Intl.NumberFormat('ko-KR', { style: 'currency', currency: 'KRW' }).format(value);

const formatNumber = (value: number) => value.toLocaleString('ko-KR');

const formatRate = (value: number) => `${value.toFixed(1)}%`;

const METRICS: { key: keyof CampaignMetrics; label: string }[] = [
  { key: 'click', label: '클릭수' },
  { key: 'postbackSuccess', label: '포스트백 성공' },
  { key: 'conversionRate', label: 'CVR' },
  { key: 'appliedUnitPrice', label: '적용 단가' },
  { key: 'revenue', label: '매출' },
];

// "전체" 합산은 서로 다른 provider/campaign의 단가가 섞여 의미가 없으므로 적용 단가는 표시하지 않는다.
const getMetrics = (provider: string) =>
  provider === TOTAL_PROVIDER ? METRICS.filter((m) => m.key !== 'appliedUnitPrice') : METRICS;

const cellKey = (provider: string, campaign: string) => `${provider}::${campaign}`;

export default function NaverDirectDetailStatsTable({ data, providerCampaigns: campaignList, startDate, endDate }: Props) {
  const tableRef = useRef<HTMLTableElement>(null);

  const filename = useMemo(
    () => `naver_direct_detail_${startDate.format('YYMMDD')}-${endDate.format('YYMMDD')}`,
    [startDate, endDate]
  );

  // 공급사(provider) -> 그 아래 캠페인(code) 목록. 마스터 목록(campaign/list) 기준으로 고정 — 데이터 유무와 무관하게 항상 전체 표시.
  // "전체" 합산 그룹은 백엔드에서 날짜별로 합성하는 가상 행이라 마스터 목록에 없으므로 맨 왼쪽에 직접 고정.
  const providerCampaigns = useMemo(() => {
    const map = new Map<string, Set<string>>();
    campaignList.forEach((row) => {
      const campaigns = map.get(row.providerID) ?? new Set<string>();
      campaigns.add(row.campaignType);
      map.set(row.providerID, campaigns);
    });
    const rest = Array.from(map.entries())
      .sort(([a], [b]) => a.localeCompare(b))
      .map(([provider, campaigns]) => ({ provider, campaigns: Array.from(campaigns).sort() }));

    return [{ provider: TOTAL_PROVIDER, campaigns: [TOTAL_PROVIDER] }, ...rest];
  }, [campaignList]);

  const pivotData = useMemo<PivotRow[]>(() => {
    const map = new Map<string, PivotRow>();
    data.forEach((row) => {
      let pivotRow = map.get(row.date);
      if (!pivotRow) {
        pivotRow = { date: row.date };
        map.set(row.date, pivotRow);
      }
      pivotRow[cellKey(row.provider, row.campaign)] = {
        click: row.click,
        postbackSuccess: row.postbackSuccess,
        conversionRate: row.conversionRate,
        appliedUnitPrice: row.appliedUnitPrice,
        revenue: row.revenue,
      };
    });
    return Array.from(map.values());
  }, [data]);

  const { sorted, sortDirection, toggleSort } = useDateSort(pivotData);

  const cellSx = { borderLeft: '1px solid #e2e8f0' };
  const totalGroupSx = { backgroundColor: '#eef2ff' };

  const formatMetric = (m: (typeof METRICS)[number], value: number | null) => {
    if (value == null) return '-';
    if (m.key === 'conversionRate') return formatRate(value);
    if (m.key === 'appliedUnitPrice' || m.key === 'revenue') return formatCurrency(value);
    return formatNumber(value);
  };

  return (
    <Box sx={{ mt: 2 }}>
      <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 1 }}>
        <TableActionsMenu tableRef={tableRef} filename={filename} />
      </Box>
      <TableContainer sx={{ overflowX: 'auto' }}>
        <Table ref={tableRef} size="small" sx={{ whiteSpace: 'nowrap' }}>
          <TableHead>
            {/* 1행: 날짜 + 공급사명 (colspan) */}
            <TableRow sx={{ backgroundColor: '#f1f5f9' }}>
              <TableCell rowSpan={3} sx={{ verticalAlign: 'middle', fontWeight: 700 }}>
                <TableSortLabel active direction={sortDirection} onClick={toggleSort}>
                  날짜
                </TableSortLabel>
              </TableCell>
              {providerCampaigns.map(({ provider, campaigns }) => (
                <TableCell
                  key={provider}
                  colSpan={campaigns.length * getMetrics(provider).length}
                  align="center"
                  sx={{
                    ...cellSx,
                    fontWeight: 700,
                    ...(provider === TOTAL_PROVIDER ? totalGroupSx : undefined),
                  }}
                >
                  {provider}
                </TableCell>
              ))}
            </TableRow>
            {/* 2행: 캠페인명 (colspan) */}
            <TableRow sx={{ backgroundColor: '#f8fafc' }}>
              {providerCampaigns.map(({ provider, campaigns }) =>
                campaigns.map((campaign) => (
                  <TableCell
                    key={cellKey(provider, campaign)}
                    colSpan={getMetrics(provider).length}
                    align="center"
                    sx={{
                      ...cellSx,
                      fontWeight: 600,
                      ...(provider === TOTAL_PROVIDER ? totalGroupSx : undefined),
                    }}
                  >
                    {campaign === TOTAL_PROVIDER ? campaign : getNaverCampaignLabel(campaign)}
                  </TableCell>
                ))
              )}
            </TableRow>
            {/* 3행: 지표명 반복 */}
            <TableRow sx={{ backgroundColor: '#f8fafc' }}>
              {providerCampaigns.map(({ provider, campaigns }) =>
                campaigns.map((campaign) =>
                  getMetrics(provider).map((m) => (
                    <TableCell
                      key={`${cellKey(provider, campaign)}-${m.key}`}
                      align="right"
                      sx={{
                        ...(m.key === 'click' ? cellSx : undefined),
                        ...(provider === TOTAL_PROVIDER ? totalGroupSx : undefined),
                      }}
                    >
                      {m.label}
                    </TableCell>
                  ))
                )
              )}
            </TableRow>
          </TableHead>
          <TableBody>
            {sorted.map((row) => (
              <TableRow key={row.date} sx={{ '&:hover': { backgroundColor: '#f8fafc' } }}>
                <TableCell>{row.date}</TableCell>
                {providerCampaigns.map(({ provider, campaigns }) =>
                  campaigns.map((campaign) => {
                    const metrics = row[cellKey(provider, campaign)] as CampaignMetrics | undefined;
                    return getMetrics(provider).map((m) => (
                      <TableCell
                        key={`${cellKey(provider, campaign)}-${m.key}`}
                        align="right"
                        sx={{
                          ...(m.key === 'click' ? cellSx : undefined),
                          ...(m.key === 'revenue' ? { fontWeight: 700, color: '#64748b' } : undefined),
                          ...(provider === TOTAL_PROVIDER ? { backgroundColor: '#f5f7ff' } : undefined),
                        }}
                      >
                        {metrics == null ? '-' : formatMetric(m, metrics[m.key])}
                      </TableCell>
                    ));
                  })
                )}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
}
